import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { getSessionTenantId } from '@/lib/auth-helpers';
import { COLOR_PALETTE } from '../types';
import type { ContactCategoryRow, ContactTagRow } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** Gera slug a partir de um label (remove acentos, espaços → hífens) */
function toSlug(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .slice(0, 50);
}

// ─── Tags ─────────────────────────────────────────────────────────────────────
export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      label,
      colorIndex,
    }: {
      label: string;
      colorIndex?: number;
    }): Promise<ContactTagRow> => {
      const tenantId = await getSessionTenantId();
      const slug = toSlug(label);
      const color_class = COLOR_PALETTE[(colorIndex ?? 0) % COLOR_PALETTE.length];

      // upsert: se já existe o slug, retorna o existente
      const { data, error } = await db
        .from('tenant_contact_tags')
        .upsert(
          { tenant_id: tenantId, label, slug, color_class },
          { onConflict: 'tenant_id,slug' },
        )
        .select()
        .single();
      if (error) throw error;
      return data as ContactTagRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao criar tag: ${e.message}`);
    },
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      label,
      color_class,
    }: {
      id: string;
      label: string;
      color_class: string;
    }): Promise<ContactTagRow> => {
      const { data, error } = await db
        .from('tenant_contact_tags')
        .update({ label, color_class })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as ContactTagRow;
    },
    onSuccess: () => {
      toast.success('Tag atualizada!');
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao atualizar tag: ${e.message}`);
    },
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db
        .from('tenant_contact_tags')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Tag removida.');
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao remover tag: ${e.message}`);
    },
  });
}

// ─── Tags de plataforma (superadmin) ─────────────────────────────────────────
export function useCreatePlatformTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      label,
      colorIndex,
    }: {
      label: string;
      colorIndex?: number;
    }): Promise<ContactTagRow> => {
      const slug = toSlug(label);
      const color_class = COLOR_PALETTE[(colorIndex ?? 0) % COLOR_PALETTE.length];
      const { data, error } = await db
        .from('tenant_contact_tags')
        .insert({ tenant_id: null, label, slug, color_class })
        .select()
        .single();
      if (error) throw error;
      return data as ContactTagRow;
    },
    onSuccess: () => {
      toast.success('Tag de plataforma criada!');
      void qc.invalidateQueries({ queryKey: ['contact-tags'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao criar tag: ${e.message}`);
    },
  });
}

// ─── Categorias ───────────────────────────────────────────────────────────────
export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      label,
      colorIndex,
    }: {
      label: string;
      colorIndex?: number;
    }): Promise<ContactCategoryRow> => {
      const tenantId = await getSessionTenantId();
      const slug = toSlug(label);
      const color_class = COLOR_PALETTE[(colorIndex ?? 0) % COLOR_PALETTE.length];

      const { data, error } = await db
        .from('tenant_contact_categories')
        .insert({ tenant_id: tenantId, label, slug, color_class })
        .select()
        .single();
      if (error) throw error;
      return data as ContactCategoryRow;
    },
    onSuccess: () => {
      toast.success('Categoria criada!');
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao criar categoria: ${e.message}`);
    },
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      label,
      color_class,
    }: {
      id: string;
      label: string;
      color_class: string;
    }): Promise<ContactCategoryRow> => {
      const { data, error } = await db
        .from('tenant_contact_categories')
        .update({ label, color_class })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as ContactCategoryRow;
    },
    onSuccess: () => {
      toast.success('Categoria atualizada!');
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao atualizar categoria: ${e.message}`);
    },
  });
}

// ─── Categorias de plataforma (superadmin) ────────────────────────────────────
export function useCreatePlatformCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      label,
      colorIndex,
    }: {
      label: string;
      colorIndex?: number;
    }): Promise<ContactCategoryRow> => {
      const slug = toSlug(label);
      const color_class = COLOR_PALETTE[(colorIndex ?? 0) % COLOR_PALETTE.length];
      const { data, error } = await db
        .from('tenant_contact_categories')
        .insert({ tenant_id: null, label, slug, color_class })
        .select()
        .single();
      if (error) throw error;
      return data as ContactCategoryRow;
    },
    onSuccess: () => {
      toast.success('Categoria de plataforma criada!');
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao criar categoria: ${e.message}`);
    },
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db
        .from('tenant_contact_categories')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Categoria removida.');
      void qc.invalidateQueries({ queryKey: ['contact-categories'] });
    },
    onError: (e: Error) => {
      toast.error(`Erro ao remover categoria: ${e.message}`);
    },
  });
}
